import { Link } from 'react-router-dom';
import { AsyncBoundary, Card } from '@kollez/design-system';
import { useMyAssignments } from '@/lib/queries';

/**
 * /assignments (Page 66/N) — student's assessment-assignment inbox.
 *
 * The AssessmentAssignment schema has real data ingested yesterday
 * (dummy-assessment-ecosystem.ts). GET /students/me/assignments serves
 * this collection but no page consumed it — assignments were only
 * discoverable via notification links. Now surfaced as a dedicated
 * inbox under the student's "Get ready" group.
 *
 * A row shows: exam name, window, assignedBy, expiresAt countdown, and
 * a Take-now button that hard-navigates to /assess/take/:examId (which
 * uses `assignmentId` when starting the attempt).
 */

function daysUntil(iso?: string): { label: string; tone: 'danger' | 'warning' | 'info' | 'default' } {
  if (!iso) return { label: '—', tone: 'default' };
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return { label: 'Expired', tone: 'danger' };
  const days = Math.ceil(diff / 86_400_000);
  if (days === 0) return { label: 'Due today', tone: 'warning' };
  if (days <= 2) return { label: `${days}d left`, tone: 'warning' };
  if (days <= 7) return { label: `${days}d left`, tone: 'info' };
  return { label: `${days}d left`, tone: 'default' };
}

export function StudentAssignmentsPage() {
  const { data, isLoading, error, refetch } = useMyAssignments({ sortBy: 'expiresAt', sortDir: 'asc', limit: 100 });
  const rows = data?.assignments ?? [];

  const now = Date.now();
  const dueSoon = rows.filter((r) => r.expiresAt && new Date(r.expiresAt).getTime() - now < 3 * 86_400_000).length;
  const notStarted = rows.filter((r) => !r.assignee?.startedAt).length;
  const inProgress = rows.filter((r) => r.assignee?.startedAt && !r.assignee?.submittedAt).length;

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Get ready</p>
          <h1 className="h-page">Your assignments</h1>
          <p className="subhead">
            Assessments your placement office or a partner has directly assigned to you.
            {rows.length > 0 && (
              <> · <b>{notStarted}</b> not started · <b>{inProgress}</b> in progress · <b>{dueSoon}</b> due within 3 days</>
            )}
          </p>
        </div>
      </div>

      <AsyncBoundary
        kind="cards"
        loading={isLoading}
        error={error}
        empty={!isLoading && rows.length === 0}
        emptyTitle="No assignments yet"
        emptyHint="When your placement office or a partner assigns a test to you, it shows up here with a due date."
        onRetry={() => void refetch()}
      >
        <div className="grid gap-3">
          {rows.map((r) => {
            const dueTag = daysUntil(r.expiresAt);
            const assessmentId = r.assessment?._id;
            const started = Boolean(r.assignee?.startedAt);
            const submitted = Boolean(r.assignee?.submittedAt);
            return (
              <Card key={r._id} padding="lg">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="cell-title">{r.assessment?.name ?? 'Assessment'}</div>
                    {/* Drive attach context — set when the assessment is wired to
                        a specific drive stage. Rendering the drive name here
                        makes it obvious this test gates a specific application. */}
                    {typeof r.drive === 'object' && r.drive?.driveName && (
                      <p className="cell-sub m-0 mt-1">
                        for <b>{r.drive.driveName}</b>
                        {r.stageKey && ` · stage ${r.stageKey}`}
                      </p>
                    )}
                    <p className="cell-sub m-0 mt-1">
                      {r.assessment?.description ?? 'No description'}
                    </p>
                    <div className="cell-sub mt-2 flex gap-3 flex-wrap">
                      {r.expiresAt && (
                        <span className={`badge ${dueTag.tone === 'danger' ? 'badge-danger' : dueTag.tone === 'warning' ? 'badge-warning' : dueTag.tone === 'info' ? 'badge-info' : ''}`}>
                          {dueTag.label}
                        </span>
                      )}
                      {submitted && <span className="badge badge-success">Submitted</span>}
                      {started && !submitted && <span className="badge badge-info">In progress</span>}
                      {!started && !submitted && dueTag.tone !== 'danger' && <span className="badge">Not started</span>}
                      {r.assessment?.scoring?.totalMarks && (
                        <span className="cell-sub">
                          Total {r.assessment.scoring.totalMarks} marks
                          {r.assessment.scoring.passingMarks
                            ? ` · pass ${r.assessment.scoring.passingMarks}`
                            : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {assessmentId && !submitted && dueTag.tone !== 'danger' && (
                    <Link
                      to={`/assess/take/${assessmentId}?assignmentId=${r._id}`}
                      className="btn btn-primary"
                    >
                      {started ? 'Resume' : 'Take now'}
                    </Link>
                  )}
                  {submitted && r.assignee?.attemptId && (
                    <Link
                      to={`/assess/attempts/${r.assignee.attemptId}`}
                      className="btn btn-secondary"
                    >
                      Review
                    </Link>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </AsyncBoundary>
    </div>
  );
}
